import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  resource,
  signal,
} from '@angular/core';
import {
  askFn,
  ButtonComponent,
  IconComponent,
  modalFn,
  ResolvableModal,
  SoftSlotDirective,
  TranslatePipe,
  translator,
} from '@softline/ui-core';
import { requestFn } from '@softline/application';
import { ResourceContainerComponent } from '@softapps/core';
import { BatchInfoComponent } from '@softapps/lager/core';
import { PickingScanRepository } from '../../repository/picking-scan.repository';
import { ScanCorrectionService } from '../../services/scan-correction.service';
import { Bewekommscan } from '../../types/bewekommscan';
import { KommissionsauftragKopf } from '../../types/kommissionsauftrag';
import { ScanCorrectionModal } from '../scan-correction-modal/scan-correction-modal';

@Component({
  selector: 'soft-scanned-quantities-modal',
  imports: [
    ButtonComponent,
    IconComponent,
    SoftSlotDirective,
    ResourceContainerComponent,
    TranslatePipe,
    BatchInfoComponent,
  ],
  templateUrl: './scanned-quantities-modal.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScannedQuantitiesModal extends ResolvableModal<KommissionsauftragKopf | null> {
  readonly idkopfkomm = input.required<number>();

  readonly #repository = inject(PickingScanRepository);
  readonly #correction = inject(ScanCorrectionService);
  readonly #modal = modalFn();
  readonly #ask = askFn();
  readonly #request = requestFn();
  readonly #t = translator();

  readonly scans = resource({
    params: () => this.idkopfkomm(),
    loader: ({ params }) => this.#repository.loadAll(params),
  });
  readonly rows = computed(() => (this.scans.hasValue() ? this.scans.value() : []));
  readonly updatedOrder = signal<KommissionsauftragKopf | null>(null);

  constructor() {
    super();
    effect(() => {
      const order = this.updatedOrder();
      if (order && this.scans.hasValue() && this.rows().length === 0) this.resolve(order);
    });
  }

  async edit(scan: Bewekommscan): Promise<void> {
    const result = await this.#modal({
      component: ScanCorrectionModal,
      title: this.#t('#KOMMISSIONIERUNG.DIALOGS.SCAN_KORREKTUR.TITLE_EDIT')(),
      separateContentSection: true,
      dismiss: true,
      data: { scan, mode: 'edit' },
    });
    if (!result || result === 'DISMISSED') return;
    await this.applyCorrection(scan, result.menge);
  }

  async remove(scan: Bewekommscan): Promise<void> {
    const answer = await this.#ask({
      title: this.#t('#KOMMISSIONIERUNG.DIALOGS.ERFASSTE_MENGEN.REMOVE_TITLE'),
      question: this.#t('#KOMMISSIONIERUNG.DIALOGS.ERFASSTE_MENGEN.REMOVE_QUESTION'),
      dismiss: true,
      dismissOnNavigation: false,
    });
    if (answer !== 'YES') return;
    await this.applyCorrection(scan, 0);
  }

  close(): void {
    this.resolve(this.updatedOrder());
  }

  private async applyCorrection(scan: Bewekommscan, menge: number): Promise<void> {
    const order = await this.#request({
      request: () => this.#correction.apply(this.idkopfkomm(), scan, menge),
      options: {
        type: 'blocking',
        successMessage: this.#t(
          menge <= 0
            ? '#KOMMISSIONIERUNG.DIALOGS.ERFASSTE_MENGEN.SUCCESS_REMOVED'
            : '#KOMMISSIONIERUNG.DIALOGS.ERFASSTE_MENGEN.SUCCESS_REDUCED'
        ),
      },
    });
    if (!order) return;
    this.updatedOrder.set(order);
    this.scans.reload();
  }
}
